import type { LucideIcon } from 'lucide-react';
import {
  Store,
  Users,
  Newspaper,
  Database,
  Shield,
  HelpCircle,
  Boxes,
  Banknote,
} from 'lucide-react';
import { supportNav } from '../modules/support/support.nav';
import { checklistsNav } from '../modules/checklists/checklists.nav';

export interface NavItem {
  id: string;
  label: string;
  path: string;
  icon?: LucideIcon;
  capability?: { domain: string; action: string };
}

export interface NavModule {
  id: string;
  label: string;
  icon: LucideIcon;
  capability: { domain: string; action: string };
  children: NavItem[];
  adminOnly?: boolean;
  /** Restrict the module to specific environments. Omit to show in all envs. */
  envs?: string[];
}

/**
 * Navigation registry — module-level navigation.
 * Sub-navigation (tabs, sections) happens within entity detail pages, not in the sidebar.
 */
export const navigationModules: NavModule[] = [
  {
    id: 'stores',
    label: 'Restaurants',
    icon: Store,
    capability: { domain: 'stores', action: 'read' },
    children: [
      { id: 'store-search', label: 'Search & Browse', path: '/stores' },
      { id: 'store-equipment', label: 'Equipment & Routines', path: '/stores/equipment' },
      { id: 'store-timezone', label: 'Timezone Tools', path: '/stores/timezone' },
      {
        id: 'store-create-test',
        label: 'Create Test Restaurants',
        path: '/stores/create-test',
        capability: { domain: 'stores', action: 'write' },
      },
    ],
  },
  {
    id: 'users',
    label: 'Users',
    icon: Users,
    capability: { domain: 'users', action: 'read' },
    children: [{ id: 'user-search', label: 'Search & Browse', path: '/users' }],
  },
  {
    id: 'content',
    label: 'Content',
    icon: Newspaper,
    capability: { domain: 'feeds', action: 'read' },
    children: [{ id: 'feed-search', label: 'Feed Search', path: '/feeds' }],
  },
  {
    id: 'microapps',
    label: 'MicroApps',
    icon: Boxes,
    capability: { domain: 'microapps', action: 'read' },
    children: [{ id: 'microapp-configs', label: 'Configurations', path: '/microapps' }],
  },
  {
    id: 'queries',
    label: 'Query Explorer',
    icon: Database,
    capability: { domain: 'queries', action: 'read' },
    children: [{ id: 'query-explorer', label: 'Explorer', path: '/queries' }],
  },
  {
    id: 'billing',
    label: 'Billing',
    icon: Banknote,
    capability: { domain: 'billing', action: 'read' },
    envs: ['prod'],
    children: [
      { id: 'billing-dashboard', label: 'Dashboard', path: '/billing' },
      { id: 'billing-grid', label: 'Monthly grid', path: '/billing/grid' },
      { id: 'billing-dormant', label: 'Dormant stores', path: '/billing/dormant' },
      { id: 'billing-pricing', label: 'Pricing config', path: '/billing/pricing' },
      { id: 'billing-contracts', label: 'Contracts', path: '/billing/contracts' },
    ],
  },
  checklistsNav,
  supportNav,
  {
    id: 'admin',
    label: 'Admin',
    icon: Shield,
    capability: { domain: 'admin', action: 'read' },
    adminOnly: true,
    children: [
      {
        id: 'admin-users',
        label: 'User Management',
        path: '/admin/users',
        capability: { domain: 'admin', action: 'write' },
      },
      {
        id: 'admin-audit',
        label: 'Audit Log',
        path: '/admin/audit',
        capability: { domain: 'audit', action: 'read' },
      },
      { id: 'admin-health', label: 'System Health', path: '/admin/health' },
      { id: 'admin-analytics', label: 'Platform Analytics', path: '/admin/analytics' },
    ],
  },
  {
    id: 'help',
    label: 'Help & Guides',
    icon: HelpCircle,
    capability: { domain: 'ai', action: 'read' },
    children: [{ id: 'help-guides', label: 'Guides', path: '/help' }],
  },
];

/**
 * Section grouping for the sidebar (design-system "nav__section" pattern).
 * Modules are rendered in section order; a module not listed here falls back
 * to the last section. Dashboard is pinned above all sections in the Sidebar.
 */
export type NavSectionId = 'manage' | 'operate' | 'admin';

export const NAV_SECTIONS: { id: NavSectionId; label: string; moduleIds: string[] }[] = [
  { id: 'manage', label: 'Manage', moduleIds: ['stores', 'users', 'content', 'microapps'] },
  { id: 'operate', label: 'Operate', moduleIds: ['queries', 'billing', 'support', 'checklists'] },
  { id: 'admin', label: 'Admin', moduleIds: ['admin', 'help'] },
];
